import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type NotificationChannel = 'whatsapp' | 'push' | 'manual';

export interface NotificationLogEntry {
  id: string;
  personal_id: string;
  student_ids: string[];
  category: string | null;
  template_id: string | null;
  message_text: string;
  channel: NotificationChannel;
  sent_at: string;
}

export function useNotificationLog(personalId: string, limit: number = 50) {
  const queryClient = useQueryClient();

  const { data: log = [], isLoading: loading } = useQuery({
    queryKey: ['notification-log', personalId, limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notification_log')
        .select('*')
        .order('sent_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data as NotificationLogEntry[];
    },
    enabled: !!personalId,
    staleTime: 60 * 1000,
  });

  const { mutateAsync: logNotification } = useMutation({
    mutationFn: async (input: {
      student_ids: string[];
      category: string | null;
      template_id: string | null;
      message_text: string;
      channel: NotificationChannel;
    }) => {
      const { data, error } = await supabase
        .from('notification_log')
        .insert({ ...input, personal_id: personalId })
        .select()
        .single();
      if (error) throw error;
      return data as NotificationLogEntry;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notification-log', personalId] }),
  });

  return { log, loading, logNotification };
}
